import { normalizeFreeTicketRequirements, formatFreeEntryRequirement, analyzeFreeTicketAffordability } from './freeTicketRequirements';
import { nftRowHasGoldenConfigured, nftRowHasNormalConfigured } from './nftBonusConfig';

/**
 * Build a structured list of blockers / hints for free play on an event.
 */
export function computeFreePlayEligibility({
  user,
  account,
  balances,
  balancesLoading = false,
  minFreeTickets = 1,
  minGoldenTickets = 0,
  locked = false,
  isResolved = false,
  nftBonuses = [],
  mode = 'create',
}) {
  const entryReqs = normalizeFreeTicketRequirements(minFreeTickets, minGoldenTickets);
  const items = [];
  let canEnter = true;

  if (isResolved) {
    canEnter = false;
    return { canEnter, items, entryReqs, primaryBlocker: null };
  }

  if (locked) {
    items.push({
      severity: 'error',
      code: 'locked',
      title: 'Predictions locked',
      message: 'This event is locked — free picks are no longer accepted.',
    });
    canEnter = false;
  }

  if (!user) {
    items.push({
      severity: 'warning',
      code: 'login',
      title: 'Log in required',
      message: 'Sign in to your account to place a free prediction.',
    });
    canEnter = false;
    return { canEnter, items, entryReqs, primaryBlocker: items[0] };
  }

  if (!user.emailVerified) {
    items.push({
      severity: 'warning',
      code: 'email',
      title: user.needsReverification ? 'Email verification expired' : 'Verify your email',
      message: user.needsReverification
        ? 'Re-verify your email to continue playing free predictions.'
        : 'Verify your email once to unlock free predictions (6-digit code).',
    });
    canEnter = false;
  }

  const normalHave = balancesLoading ? null : Math.max(0, balances?.normalTickets ?? 0);
  const goldenHave = balancesLoading ? null : Math.max(0, balances?.goldenTickets ?? 0);
  const minTotal = entryReqs.minTotalStake;

  if (!balancesLoading && user?.emailVerified && !locked) {
    const afford = analyzeFreeTicketAffordability({
      minFreeTickets: entryReqs.minFreeTickets,
      minGoldenTickets: entryReqs.minGoldenTickets,
      normalHave,
      goldenHave,
      stake: minTotal,
    });
    if (!afford.ok) {
      const isGolden = afford.code === 'golden_short' || afford.code === 'golden_only_short';
      items.push({
        severity: 'error',
        code: afford.code,
        title: isGolden ? 'Not enough golden tickets' : 'Not enough tickets',
        message: afford.message,
        need: afford.need,
        have: afford.have,
        short: afford.short,
      });
      canEnter = false;
    }
  }

  const nftGoldenConfigured = (nftBonuses || []).some((n) => nftRowHasGoldenConfigured(n));
  const nftNormalConfigured = (nftBonuses || []).some((n) => nftRowHasNormalConfigured(n));
  const hasLinkedWallet = !!(user?.wallets?.length || user?.walletAddress);

  if (user && (nftGoldenConfigured || nftNormalConfigured) && !hasLinkedWallet) {
    items.push({
      severity: 'info',
      code: 'wallet_nft',
      title: 'Link wallet for NFT bonuses',
      message: nftGoldenConfigured
        ? 'Link your wallet once to verify NFT holdings. Golden tickets accrue automatically while you hold eligible tokens — no need to reconnect each visit. Selling stops future golden grants.'
        : 'Link your wallet once to verify NFT holdings and receive bonus tickets automatically in the background.',
    });
  }

  if (user?.emailVerified && !locked && entryReqs.valid && (entryReqs.minFreeTickets > 0 || entryReqs.minGoldenTickets > 0)) {
    items.push({
      severity: 'info',
      code: 'entry_req',
      title: 'Entry requirement',
      message: formatFreeEntryRequirement(entryReqs),
    });
  }

  const primaryBlocker = items.find((i) => i.severity === 'error' || i.severity === 'warning') || null;

  return { canEnter: canEnter && user?.emailVerified, items, entryReqs, primaryBlocker };
}

export function freePlayBlockerMessage(eligibility) {
  if (!eligibility?.primaryBlocker) return null;
  return eligibility.primaryBlocker.message;
}
